
import React, { useState, useEffect } from 'react';
import {
    Box, VStack, HStack, Text, Card, CardBody, CardHeader,
    Spinner, Center, Avatar, Grid, GridItem, Button,
    Input, InputGroup, InputLeftElement, Flex, Badge
} from '@chakra-ui/react';
import { FaInstagram, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { customAuthAxios } from '../../../utils/customAuthAxios';

// DMメッセージの型定義
type InstagramDmContent = {
    id: string;
    sender_name: string;
    sender_id: string;
    message: string;
    timestamp: string;
    is_read: boolean;
    sender_profile_pic?: string;
};

export const InstagramDmHistory: React.FC = () => {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const navigate = useNavigate();
    const [dmMessages, setDmMessages] = useState<InstagramDmContent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // DM履歴を取得する関数
    const fetchDmHistory = async () => {
        if (!restaurantId) return;

        try {
            setIsLoading(true);
            const response = await customAuthAxios.get(
                `external_api/instagram-dm/${restaurantId}/history/`
            );
            setDmMessages(response.data);
        } catch (error) {
            console.error('DM履歴取得エラー:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDmHistory();
    }, [restaurantId]);

    // 検索フィルタ
    const filteredMessages = dmMessages.filter(message =>
        message.sender_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        message.message.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 送信者ごとにグループ化
    const groupedMessages = filteredMessages.reduce((groups, message) => {
        const senderId = message.sender_id;
        if (!groups[senderId]) {
            groups[senderId] = {
                sender_name: message.sender_name,
                sender_id: message.sender_id,
                sender_profile_pic: message.sender_profile_pic,
                messages: [],
                unreadCount: 0,
                lastMessage: message
            };
        }
        groups[senderId].messages.push(message);
        if (!message.is_read) {
            groups[senderId].unreadCount++;
        }
        // 最新のメッセージを保持
        if (new Date(message.timestamp) > new Date(groups[senderId].lastMessage.timestamp)) {
            groups[senderId].lastMessage = message;
        }
        return groups;
    }, {} as Record<string, {
        sender_name: string;
        sender_id: string;
        sender_profile_pic?: string;
        messages: InstagramDmContent[];
        unreadCount: number;
        lastMessage: InstagramDmContent;
    }>);

    // 送信者リストを最新メッセージ順でソート
    const sendersList = Object.values(groupedMessages).sort((a, b) =>
        new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime()
    );

    return (
        <Box p={6} maxW="1200px" mx="auto">
            {/* ヘッダー */}
            <VStack spacing={6} align="stretch">
                <HStack spacing={4}>
                    <Button
                        leftIcon={<FaArrowLeft size="20px" />}
                        variant="ghost"
                        onClick={() => navigate(-1)}
                    >
                        戻る
                    </Button>
                    <HStack spacing={2}>
                        <FaInstagram size={24} />
                        <Text fontSize="2xl" fontWeight="bold">DM履歴一覧</Text>
                    </HStack>
                </HStack>

                {/* 検索バー */}
                <InputGroup maxW="400px">
                    <InputLeftElement>
                        <FaSearch color="gray" />
                    </InputLeftElement>
                    <Input
                        placeholder="送信者名またはメッセージで検索..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>

                {/* メイン内容 */}
                {isLoading ? (
                    <Center h="300px">
                        <Spinner size="xl" color="blue.500" />
                    </Center>
                ) : sendersList.length === 0 ? (
                    <Center h="300px">
                        <Text color="gray.500">DM履歴がありません</Text>
                    </Center>
                ) : (
                    <Grid templateColumns={["1fr", "1fr", "repeat(2, 1fr)"]} gap={6}>
                        {sendersList.map((sender) => (
                            <GridItem key={sender.sender_id}>
                                <Card
                                    cursor="pointer"
                                    _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
                                    transition="all 0.2s"
                                    onClick={() => {
                                        // 個別のDM詳細画面に遷移（必要に応じて実装）
                                        console.log('Click sender:', sender.sender_id);
                                    }}
                                >
                                    <CardHeader pb={2}>
                                        <HStack spacing={3}>
                                            <Avatar
                                                size="md"
                                                src={sender.sender_profile_pic}
                                                name={sender.sender_name}
                                            />
                                            <Flex flex={1} justify="space-between" align="center">
                                                <VStack align="start" spacing={0}>
                                                    <Text fontWeight="bold" fontSize="md">
                                                        {sender.sender_name}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        @{sender.sender_id}
                                                    </Text>
                                                </VStack>
                                                {sender.unreadCount > 0 && (
                                                    <Badge colorScheme="red" borderRadius="full">
                                                        {sender.unreadCount}
                                                    </Badge>
                                                )}
                                            </Flex>
                                        </HStack>
                                    </CardHeader>
                                    <CardBody pt={0}>
                                        <VStack align="stretch" spacing={2}>
                                            <Text fontSize="sm" noOfLines={2} color="gray.700">
                                                {sender.lastMessage.message}
                                            </Text>
                                            <HStack justify="space-between">
                                                <Text fontSize="xs" color="gray.500">
                                                    {new Date(sender.lastMessage.timestamp).toLocaleDateString('ja-JP', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {sender.messages.length}件のメッセージ
                                                </Text>
                                            </HStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </GridItem>
                        ))}
                    </Grid>
                )}
            </VStack>
        </Box>
    );
};